import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, DollarSign } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../ui/dialog';
import { Switch } from '../ui/switch';
import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';

const TaxesTab = () => {
  const { getAuthHeaders } = useAuth();
  const [taxes, setTaxes] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [formData, setFormData] = useState({
    tax_name: '',
    tax_rate: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadTaxes();
  }, []);

  const loadTaxes = async () => {
    try {
      const response = await axios.get(`${API}/taxes`, {
        headers: getAuthHeaders()
      });
      setTaxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cargar impuestos'));
    }
  };

  const handleOpenDialog = () => {
    setEditingTax(null);
    setFormData({ tax_name: '', tax_rate: 0 });
    setIsDialogOpen(true);
  };

  const handleEdit = (tax) => {
    setEditingTax(tax);
    setFormData({
      tax_name: tax.tax_name,
      tax_rate: tax.tax_rate * 100 // Convert to percentage
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const taxData = {
        tax_name: formData.tax_name,
        tax_rate: formData.tax_rate / 100 // Convert to decimal
      };

      if (editingTax) {
        await axios.put(`${API}/taxes/${editingTax.id}`, taxData, {
          headers: getAuthHeaders()
        });
        toast.success('Impuesto actualizado exitosamente');
      } else {
        await axios.post(`${API}/taxes`, taxData, {
          headers: getAuthHeaders()
        });
        toast.success('Impuesto creado exitosamente');
      }

      await loadTaxes();
      handleCloseDialog();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al guardar impuesto'));
    }
    setLoading(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      setEditingTax(null);
      setFormData({ tax_name: '', tax_rate: 0 });
    }, 100);
  };

  const handleToggleStatus = async (tax) => {
    try {
      await axios.patch(
        `${API}/taxes/${tax.id}/toggle`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Impuesto ${tax.is_active ? 'desactivado' : 'activado'} exitosamente`);
      loadTaxes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al cambiar estado'));
    }
  };

  const handleDelete = async (taxId) => {
    if (!window.confirm('¿Eliminar este impuesto?')) return;

    try {
      await axios.delete(`${API}/taxes/${taxId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Impuesto eliminado exitosamente');
      loadTaxes();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al eliminar impuesto'));
    }
  };

  return (
    <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-orange-100">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Configuración de Impuestos</h2>
            <p className="text-sm text-gray-600">Gestiona los impuestos aplicables</p>
          </div>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleOpenDialog} className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Impuesto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTax ? 'Editar Impuesto' : 'Nuevo Impuesto'}</DialogTitle>
              <DialogDescription>
                {editingTax
                  ? 'Modifica los datos del impuesto seleccionado'
                  : 'Configura un nuevo impuesto para aplicar a las órdenes'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nombre del Impuesto</Label>
                <Input
                  value={formData.tax_name}
                  onChange={(e) => setFormData({ ...formData, tax_name: e.target.value })}
                  placeholder="Ej: IVA, Propina Legal"
                  required
                  className="rounded-xl"
                />
              </div>
              <div>
                <Label>Porcentaje (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.tax_rate}
                  onChange={(e) => setFormData({ ...formData, tax_rate: parseFloat(e.target.value) })}
                  placeholder="Ej: 18"
                  required
                  className="rounded-xl"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleCloseDialog} className="flex-1 rounded-xl">
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 rounded-xl">
                  {editingTax ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {taxes.length === 0 ? (
        <div className="text-center py-12">
          <DollarSign className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">No hay impuestos configurados</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {taxes.map((tax) => (
            <Card key={tax.id} className="p-4 hover:shadow-lg transition-shadow">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{tax.tax_name}</h3>
                  <p className="text-gray-600">{(tax.tax_rate * 100).toFixed(2)}%</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">{tax.is_active ? 'Activo' : 'Inactivo'}</Label>
                    <Switch
                      checked={tax.is_active}
                      onCheckedChange={() => handleToggleStatus(tax)}
                    />
                  </div>
                  <Button size="sm" variant="outline" onClick={() => handleEdit(tax)} className="rounded-xl">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(tax.id)} className="rounded-xl text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default TaxesTab;
